// Legacy component : this is only used by the old edit portfolio modal

// Third Party
import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import camelCase from 'lodash/camelCase';

// Components
import { PrimaryButton, TertiaryButton } from '../buttons';
import { CreateKycQuestionnaire } from '../modals';
import EditPortfolioFundComparison from './EditPortfolioFundComparison';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import {
  addAltToPortfolioById,
  addFundToPortfolioById,
  addStockToPortfolioById,
  addFundsToPortfolio,
  addCustomYieldHolding,
  attachProfileToPortfolio,
  ADD_FUNDS_TO_PORTFOLIO,
  SET_IS_ATTACHING_PROFILE_TO_PORTFOLIO,
} from '../../../redux/modules/edit-portfolio';
import { addSearchItemLog } from '../../../redux/modules/activity-log';
import { setFocusedFund, resetSearch, loadQuickAddItems } from '../../../redux/modules/advanced-search';
import { getKycQuestionnaires, loadKycQuestionnaire, resetRiskProfile, SET_IS_RESETTING_RISK_PROFILE } from '../../../redux/modules/kyc-questionnaire';
import { clearComplianceAlternatives } from '../../../redux/modules/compliance-alternatives';
import { selectIsLoadingEvery, selectIsLoadingSome } from '../../../redux/modules/ui/loaders';

// Utils
import notification from '../../../utils/notification';
import { SEARCH_ITEM_SOURCE, SEARCH_ITEM_DEST } from '../../../utils/activity-log';
import { COMPARISON_ITEM_TYPES, PROXY_OPTION, ADVANCED_SEARCH_OPTIONS } from '../../../utils/constants';
import { showError } from '../../../utils/errors';
import { formatLocalizedString, formatMessage, FormattedMessage } from '../../../utils/i18n';
import QuickAddContainer from './quick-add-components/QuickAddContainer';
import EditPortfolioScreener from './EditPortfolioScreener';
import { getActiveQuestionnaire } from '../../../utils/risk-profiles';

// Types
import { FundSearchState } from '../../../redux/modules/types/fund-search';
import { PortfolioBuilderContentOption } from '../../../redux/modules/types/edit-portfolio';
import { CURRENCY } from '../../../utils/currency';
import CustomYieldParams from '@/types/custom-yield-params';
import ShelvableInstrument from '@/types/shelvable-instrument';
import CompliantInstrument from '@/types/compliant-instrument';
import PortfolioHolding from '@/types/portfolio-holding';
import Fund from '@/types/fund';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import ContactRiskProfile from '@/types/contact-risk-profile';
import QuickAddItems from '@/types/quick-add-items';
import QuickSearchItem from '@/types/quick-search-item';
import { LoadingSpinner } from '../common';
import EditPortfolioQuickSearch from './EditPortfolioQuickSearch';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

type DispatchProps = {
  addAltToPortfolioById: (id: string) => Promise<void>;
  addCustomYieldHolding: (
    config: CustomYieldParams,
    shelfInfo: ShelvableInstrument & CompliantInstrument,
    currency: string,
    holdingToReplace?: PortfolioHolding,
  ) => void;
  addFundToPortfolioById: (id: string, holdingToReplace?: PortfolioHolding) => Promise<void>;
  addStockToPortfolioById: (id: string, holdingToReplace?: PortfolioHolding) => Promise<void>;
  addSearchItemLog: (id: string, type: string, source: string, dest: string) => void;
  addFundsToPortfolio: (funds: Fund[]) => Promise<void[]>;
  setFocusedFund: (fund?: Fund) => void;
  resetSearch: () => void;
  loadQuickAddItems: () => Promise<void>;
  loadKycQuestionnaire: (questionnaire: KycQuestionnaire) => void;
  resetRiskProfile: () => void;
  attachProfileToPortfolio: (profile: Partial<PortfolioRiskProfile>) => void;
  clearComplianceAlternatives: () => void;
  getKycQuestionnaires: () => Promise<KycQuestionnaire[]>;
};

type StateProps = {
  isExpanded: boolean;
  onToggleExpand: () => void;
  setSidebarContentOption: (option: PortfolioBuilderContentOption) => void;
  fundState: FundSearchState;
  sidebarContentOption: PortfolioBuilderContentOption;
  currentQuestionnaire?: KycQuestionnaire;
  user?: Nullable<User>;
  isCompleted: boolean;
  newRiskProfile: Partial<ContactRiskProfile>;
  questionnaires: KycQuestionnaire[];
  complianceAlternatives: Fund[];
  addingToPortfolio: boolean;
  isSavingRiskProfile?: boolean;
};

type State = {
  proxyOption?: PROXY_OPTION;
  showFileUpload: boolean;
  riskProfileIsLoading: boolean;
};

type Props = DispatchProps &
  StateProps & {
    quickAddItems: QuickAddItems[];
    missingHoldingIsSelected: boolean;
    holdingSelected?: PortfolioHolding;
    missingHoldingsCount: number;
    onPin?: () => void;
    componentIsReady?: () => void;
  };

class EditPortfolioQuickAddContainer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.props.loadQuickAddItems();
  }

  fileUploaderRef = React.createRef<HTMLDivElement>();

  readonly state: State = {
    proxyOption: undefined,
    showFileUpload: false,
    riskProfileIsLoading: false,
  };

  componentDidMount() {
    const { questionnaires, user, componentIsReady } = this.props;

    if (!user) {
      return;
    }

    if (this.props.sidebarContentOption !== PortfolioBuilderContentOption.ComplianceAlternatives) {
      this.props.setSidebarContentOption(PortfolioBuilderContentOption.QuickSearch);
    }
    this.props.resetRiskProfile();

    const questionnaire = getActiveQuestionnaire(questionnaires);

    if (questionnaire) {
      this.props.loadKycQuestionnaire(questionnaire);
    }

    componentIsReady && componentIsReady();
  }

  componentDidUpdate(prevProps: Props) {
    const { sidebarContentOption, getKycQuestionnaires } = this.props;

    if (sidebarContentOption !== prevProps.sidebarContentOption && sidebarContentOption === PortfolioBuilderContentOption.RiskProfile) {
      this.setState({ riskProfileIsLoading: true });
      getKycQuestionnaires().then(() => {
        this.props.loadKycQuestionnaire(getActiveQuestionnaire(this.props.questionnaires));
        this.setState({ riskProfileIsLoading: false });
      });
    }
  }

  onAddItem = (item: QuickSearchItem) => {
    switch (item.type) {
      case COMPARISON_ITEM_TYPES.FUND: {
        return this.props
          .addFundToPortfolioById(item._id, this.props.holdingSelected)
          .then(() => {
            this.props.addSearchItemLog(item._id, item.type, SEARCH_ITEM_SOURCE.QUICKSEARCH, SEARCH_ITEM_DEST.PORTFOLIO);
          })
          .catch(showError);
      }
      case COMPARISON_ITEM_TYPES.STOCK: {
        return this.props
          .addStockToPortfolioById(item._id, this.props.holdingSelected)
          .then(() => {
            this.props.addSearchItemLog(item._id, item.type, SEARCH_ITEM_SOURCE.QUICKSEARCH, SEARCH_ITEM_DEST.PORTFOLIO);
          })
          .catch(showError);
      }
      default: {
        return Promise.reject(new Error('Unsupported search item type'));
      }
    }
  };

  onOpenFundScreener = () => {
    this.props.setSidebarContentOption(PortfolioBuilderContentOption.FundScreener);
  };

  onCloseScreener = () => {
    const { isExpanded } = this.props;

    this.setState({ proxyOption: undefined });
    this.props.setSidebarContentOption(PortfolioBuilderContentOption.QuickSearch);

    if (isExpanded) {
      this.props.onToggleExpand();
    }
  };

  onArrowClick = (fund: Fund) => {
    const { isExpanded } = this.props;

    if (!fund) {
      return;
    }

    this.props.setFocusedFund(fund);

    if (!isExpanded) {
      this.props.onToggleExpand();
    }
  };

  onToggleExpand = () => {
    const {
      fundState: { focusedFund },
    } = this.props;

    if (focusedFund) {
      this.props.setFocusedFund(undefined);
    }

    this.props.onToggleExpand();
  };

  onSelectComplianceAlternative = (fund: Fund) => {
    const { clearComplianceAlternatives } = this.props;

    this.props
      .addFundToPortfolioById(fund._id)
      .then(() => {
        clearComplianceAlternatives();
        this.onCloseScreener();
      })
      .catch(showError);
  };

  onAddToPortfolio = (fund: Fund) => {
    this.props.addFundToPortfolioById(fund._id, this.props.holdingSelected).catch(showError);
  };

  onAddFundsToPortfolio = () => {
    const {
      fundState: { selectedFunds },
    } = this.props;
    this.props.addFundsToPortfolio(selectedFunds).then(() => {
      this.onCloseScreener();
      this.props.resetSearch();
    });
  };

  onAddProxiesToPortfolio = () => {
    const {
      fundState: { selectedFunds },
    } = this.props;
    this.props.addFundsToPortfolio(selectedFunds).then(() => {
      this.onCloseScreener();
      this.props.resetSearch();
    });
  };

  addFundById = (id: string) => {
    this.props.addFundToPortfolioById(id, this.props.holdingSelected).catch(showError);
  };

  addCustomYield = (config: CustomYieldParams, shelfInfo: ShelvableInstrument & CompliantInstrument) => {
    const { user } = this.props;
    const currency: string = user?.appConfig.localCurrency || CURRENCY.CAD;

    this.props.addCustomYieldHolding(config, shelfInfo, currency, this.props.holdingSelected);
  };

  getFundScreenerActions = () => {
    const { addingToPortfolio } = this.props;
    return [
      <PrimaryButton key="action-add" loading={addingToPortfolio} compact icon="plus" onClick={this.onAddFundsToPortfolio}>
        <FormattedMessage id={`Search.Funds.${addingToPortfolio ? 'AddingToPortfolio' : 'AddToPortfolio'}`} />
      </PrimaryButton>,
    ];
  };

  getProxyScreenerActions = () => [
    <PrimaryButton key="action-add" compact icon="plus" onClick={this.onAddProxiesToPortfolio}>
      <FormattedMessage id="Search.Proxies.AddToPortfolio" />
    </PrimaryButton>,
  ];

  openProxiesScreener = (proxyOption: string) => {
    const localProxyOption = proxyOption as PROXY_OPTION;

    this.props.setSidebarContentOption(PortfolioBuilderContentOption.ProxyScreener);
    this.setState({ proxyOption: localProxyOption });
  };

  saveRiskProfile = () => {
    const { newRiskProfile } = this.props;

    if (!newRiskProfile) {
      notification.errorFromKey('DEFAULT_ERROR');
      return;
    }

    this.props.attachProfileToPortfolio(newRiskProfile);
    this.props.resetRiskProfile();
    this.props.setSidebarContentOption(PortfolioBuilderContentOption.QuickSearch);
  };

  onCancelRiskProfile = () => {
    this.props.resetRiskProfile();
    this.props.setSidebarContentOption(PortfolioBuilderContentOption.QuickSearch);
  };

  onCompareAlternatives = () => {
    const { isExpanded } = this.props;

    if (!isExpanded) {
      this.props.onToggleExpand();
    }
  };

  renderContent = () => {
    const {
      isExpanded,
      fundState,
      quickAddItems,
      sidebarContentOption,
      currentQuestionnaire,
      missingHoldingIsSelected,
      missingHoldingsCount,
      newRiskProfile,
      onPin,
      complianceAlternatives,
      clearComplianceAlternatives,
      holdingSelected,
      user,
    } = this.props;

    if (!user) {
      return null;
    }

    const { proxyOption } = this.state;

    switch (true) {
      case sidebarContentOption === PortfolioBuilderContentOption.QuickSearch: {
        return (
          <>
            <div className="quick-search-container">
              <EditPortfolioQuickSearch onAddItem={this.onAddItem} withItemScreener onScreenerButtonClick={this.onOpenFundScreener} />
            </div>
            {missingHoldingsCount > 1 ? (
              <div className="multiple-missing-holding-quick-add-message">
                <i className="icon-error displayed-icon" />
                <div>
                  {formatMessage('EditPortfolioModal.MissingMultipleHoldingsMessage')}
                  <div className="multiple-missing-holding-quick-add-message-count">
                    <strong>{missingHoldingsCount}</strong>
                    {'\u00A0'}
                    {formatMessage('EditPortfolioModal.MissingMultipleHoldingsCount')}
                  </div>
                </div>
              </div>
            ) : (
              missingHoldingIsSelected &&
              holdingSelected && (
                <div className="missing-holding-quick-add-message">
                  <i className="icon-error displayed-icon" />
                  {formatLocalizedString(holdingSelected.name)}
                  {'\u00A0'}
                  {formatMessage('EditPortfolioModal.MissingHoldingMessage')}
                </div>
              )
            )}

            <div className="quick-add-title">
              <FormattedMessage id="EditPortfolioModal.QuickAdd" />
            </div>

            <div className="quick-add-sections-container">
              {quickAddItems &&
                quickAddItems.map(items => (
                  <QuickAddContainer
                    user={user}
                    key={items.key}
                    section={items}
                    onAddFund={this.addFundById}
                    onAddCustomYield={this.addCustomYield}
                    onOpenScreener={this.openProxiesScreener}
                  />
                ))}
            </div>

            {onPin && !holdingSelected && userHasPermission(user, PERMISSIONS.PORTFOLIO_EDITOR_PINNED) && (
              <div className="quick-add-footer">
                <TertiaryButton icon="expand-bottom-right" onClick={onPin}>
                  <FormattedMessage id="EditPortfolioModal.PinnedEditor" />
                </TertiaryButton>
              </div>
            )}
          </>
        );
      }
      case sidebarContentOption === PortfolioBuilderContentOption.FundScreener: {
        return (
          <div className="quick-search-container">
            <EditPortfolioScreener
              classicVersionOnly
              showInlineAdd={!isExpanded}
              title={formatMessage('EditPortfolioModal.FundScreener')}
              onClose={this.onCloseScreener}
              onExpand={this.onToggleExpand}
              isExpanded={isExpanded}
              hideSaveFund={!isExpanded || !!fundState.focusedFund}
              onArrowClick={this.onArrowClick}
              onAddItem={this.onAddToPortfolio}
              customTableActions={this.getFundScreenerActions()}
              selectedOption={ADVANCED_SEARCH_OPTIONS.FUNDS}
              onCompareAlternatives={this.onCompareAlternatives}
            />
          </div>
        );
      }
      case sidebarContentOption === PortfolioBuilderContentOption.ProxyScreener: {
        return (
          <div className="quick-search-container">
            <EditPortfolioScreener
              classicVersionOnly
              hideSaveFund
              showInlineAdd={!isExpanded}
              title={formatMessage('EditPortfolioModal.Screener.Proxy')}
              onClose={this.onCloseScreener}
              onExpand={this.onToggleExpand}
              isExpanded={isExpanded}
              onArrowClick={this.onArrowClick}
              onAddItem={this.onAddToPortfolio}
              customTableActions={this.getProxyScreenerActions()}
              selectedOption={ADVANCED_SEARCH_OPTIONS.PROXIES}
              proxyOption={proxyOption}
              onCompareAlternatives={this.onCompareAlternatives}
            />
          </div>
        );
      }
      case sidebarContentOption === PortfolioBuilderContentOption.RiskProfile: {
        if (!currentQuestionnaire) {
          return null;
        }

        return this.state.riskProfileIsLoading ? (
          <div className="justify-content-center">
            <LoadingSpinner />
          </div>
        ) : (
          <CreateKycQuestionnaire
            isModal={false}
            riskProfiles={currentQuestionnaire.riskProfiles}
            newRiskProfile={newRiskProfile}
            minRiskCalculation={currentQuestionnaire.options.minRiskCalculation}
            scoreCalculation={currentQuestionnaire.options.scoreCalculation}
            onSave={this.saveRiskProfile}
            onClose={this.onCancelRiskProfile}
            isSaving={this.props.isSavingRiskProfile}
          />
        );
      }
      case sidebarContentOption === PortfolioBuilderContentOption.ComplianceAlternatives: {
        return (
          <EditPortfolioFundComparison
            complianceAlternatives={complianceAlternatives}
            clearComplianceAlternatives={clearComplianceAlternatives}
            selectComplianceAlternative={this.onSelectComplianceAlternative}
            onClose={this.onCloseScreener}
          />
        );
      }
      default:
        return null;
    }
  };

  render() {
    const { isExpanded, fundState, sidebarContentOption, complianceAlternatives } = this.props;

    const classes = classNames('edit-portfolio-quick-add-container', sidebarContentOption, {
      'show-sidebar-content': sidebarContentOption !== PortfolioBuilderContentOption.QuickSearch,
      'is-expanded': isExpanded,
      'show-fund-details': fundState.focusedFund,
      'show-fund-alternatives': complianceAlternatives.length > 0,
    });

    return <div className={classes}>{this.renderContent()}</div>;
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  fundState: state.advancedSearch.fund,
  quickAddItems: state.advancedSearch.quickAddItems,
  currentQuestionnaire: state.kycQuestionnaire.currentQuestionnaire,
  questionnaires: state.kycQuestionnaire.questionnaires,
  isCompleted: state.kycQuestionnaire.isCompleted,
  newRiskProfile: state.kycQuestionnaire.newRiskProfile,
  complianceAlternatives: state.complianceAlternatives,
  addingToPortfolio: selectIsLoadingEvery(state, [ADD_FUNDS_TO_PORTFOLIO.type]),
  isSavingRiskProfile: selectIsLoadingSome(state, [SET_IS_ATTACHING_PROFILE_TO_PORTFOLIO.type, SET_IS_RESETTING_RISK_PROFILE.type]),
});

export default connect(mapStateToProps, {
  addAltToPortfolioById,
  loadQuickAddItems,
  resetSearch,
  addFundToPortfolioById,
  addFundsToPortfolio,
  addCustomYieldHolding,
  addStockToPortfolioById,
  addSearchItemLog,
  setFocusedFund,
  loadKycQuestionnaire,
  resetRiskProfile,
  attachProfileToPortfolio,
  clearComplianceAlternatives,
  getKycQuestionnaires,
})(EditPortfolioQuickAddContainer);
